import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  message,
  Breadcrumb,
  Row,
  Col,
  Spin,
  Table,
  Typography,
  Statistic,
  Tag,
  Space,
  Descriptions,
  Timeline,
  Empty,
  Form,
  InputNumber,
  Drawer,
  Input,
} from "antd";
import {
  ArrowLeftOutlined,
  HomeOutlined,
  EditOutlined,
  CalendarOutlined,
  DollarOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import { campainAPIWithErrorHandling } from "../../../api/collection/campain";
import { paymentAPIWithErrorHandling } from "../../../api/payment/payment";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { neutral, spacing } from "../../../theme/tokens";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function InfoCampain() {
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [campain, setCampain] = useState(null);
  const [stats, setStats] = useState({});
  const navigate = useNavigate();
  const { id } = useParams();
  const [payLoading, setPayLoading] = useState(false);
  const [payModalVisible, setPayModalVisible] = useState(false);
  const [selectedCustomerFee, setSelectedCustomerFee] = useState(null);
  const [payForm] = Form.useForm();
  const [sendOneLoading, setSendOneLoading] = useState(null);
  // const [sendLoading, setSendLoading] = useState(false);
  // const [templatePreview, setTemplatePreview] = useState(null);
  // const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Fetch collection data on component mount
  useEffect(() => {
    if (id) {
      fetchCampainData();
      fetchCompainStats();
    }
  }, [id]);

  const fetchCampainData = async () => {
    try {
      setLoading(true);
      const response = await campainAPIWithErrorHandling.getCampainById(id);
      const campainData = response.data?.data;

      if (!campainData) {
        message.error("Không tìm thấy chiến dịch!");
        navigate("/campains");
        return;
      }

      setCampain(campainData);
    } catch (error) {
      message.error("Không thể tải thông tin chiến dịch: " + error.message);
      navigate("/campains");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompainStats = async () => {
    try {
      setStatsLoading(true);
      const response = await campainAPIWithErrorHandling.getCampainStats(id);
      const statsData = response.data?.data;

      if (statsData) {
        setStats({
          totalCustomers: statsData.total_customers || 0,
          totalPending: statsData.total_pending || 0,
          totalPaid: statsData.total_paid || 0,
          collectionRate: statsData.collection_rate || 0,
          totalAmountPaid: parseFloat(statsData.total_amount_paid || 0),
        });
      }
    } catch (error) {
      console.error("Không thể tải thống kê:", error.message);
    } finally {
      setStatsLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (!campain)
      return { status: "default", text: "Không xác định", color: "gray" };

    const statusConfig = {
      PLANNED: {
        icon: <ClockCircleOutlined />,
        color: "blue",
        text: "Nháp",
      },
      ONGOING: {
        icon: <CheckCircleOutlined />,
        color: "green",
        text: "Đang diễn ra",
      },
      CLOSED: {
        icon: <CheckCircleOutlined />,
        color: "red",
        text: "Đã kết thúc",
      },
    };

    return statusConfig[campain.status];
  };

  
  // // Handle preview template
  // const handlePreviewTemplate = async () => {
  //     if (!campain.zns_template_id) {
  //         message.warning("Vui lòng nhập Template ID");
  //         return;
  //     }

  //     try {
  //         setLoadingTemplate(true);
  //         const response = await mapZnsAPIWithErrorHandling.getTemplate({ template_id: campain.zns_template_id });

  //         if (response.data.success) {
  //             setTemplatePreview(response.data.data);
  //             message.success("Tải mẫu tin thành công");
  //         }
  //     } catch (error) {
  //         message.error(error.message || "Không thể tải mẫu tin");
  //         setTemplatePreview(null);
  //     } finally {
  //         setLoadingTemplate(false);
  //     }
  // };

  const pay = (record) => {
    setSelectedCustomerFee(record);
    payForm.setFieldsValue({
      transaction_amount: 0,
      note: "",
    });
    setPayModalVisible(true);
  };

  const handlePayment = async (values) => {
    try {
      setPayLoading(true);

      const dataPay = {
        transaction_amount: values.transaction_amount,
        note: values.note || "",
      };

      await paymentAPIWithErrorHandling.payemntManual(
        selectedCustomerFee.id,
        dataPay
      );

      message.success("Thanh toán thành công!");
      setPayModalVisible(false);
      payForm.resetFields();

      // Refresh data
      fetchCampainData();
      fetchCompainStats();
    } catch (error) {
      message.error(error.message || "Thanh toán thất bại");
    } finally {
      setPayLoading(false);
    }
  };
  
  const sendOne = async (record) => {
    try {
      setSendOneLoading(record.id);
      // const response = await mapZnsAPIWithErrorHandling.sendOneCampaignZns(
      //   record.collection_id,
      //   record.customer_id
      // );
      const response = await mapZnsAPIWithErrorHandling.sendOneCampaignZns(
        record.id
      );

      message.success(
        response?.message ?? "Gửi tin nhắc nhở qua ZNS thành công"
      );
    } catch (error) {
      message.error(error.message);
    } finally {
      setSendOneLoading(null);
    }
  };

  const getTableColumns = () => {
    if (!campain || !campain.customer_fees?.length) return [];

    return [
      {
        title: "Mã thu",
        key: "code",
        width: 150,
        align: "center",
        fixed: 'left',
        render: (_, record) => <Tag>{record.code}</Tag>,
      },
      // {
      //   title: "Mã KH",
      //   dataIndex: "customer_code",
      //   key: "customer_code",
      //   width: 120,
      //   align: "center",
      //   render: (_, record) => (
      //     <Tag>
      //       {record.customer.code ?? '_'}
      //     </Tag>
      //   ),
      // },
      {
        title: "Khách hàng",
        key: "customer",
        width: 180,
        render: (_, record) => (
          <div onClick={() => handleCusInfo(record)}
            style={{
                cursor: "pointer",
                color: "#0070D7",
            }}
          >
            <div className="font-medium">
              {record.customer?.name || `Khách hàng ${record.customer_id}`}
            </div>
          </div>
        ),
      },
      {
        title: "Người đại diện",
        dataIndex: "represent_name",
        key: "represent_name",
        width: 180,
        render: (_, record) => <div>{record.customer.represent_name}</div>,
      },
      {
        title: "Số nhà",
        dataIndex: "sonha",
        key: "sonha",
        align: "center",
        width: 120,
        render: (_, record) => (
            <div>{record?.customer?.sonha ?? '_'}</div>
        ),
      },
      {
        title: "Đơn vị",
        dataIndex: "org_id",
        key: "org_id",
        width: 200,
        render: (_, record) => {
          return (
            <div onClick={() => handleOrgInfo(campain)}>
              <Text style={{ cursor: 'pointer', color: "#0070D7" }}>
                {campain.organization?.name}
              </Text>
            </div>
          );
        }
      },
      {
        title: "Điện thoại",
        dataIndex: "customer_phone",
        key: "customer_phone",
        width: 120,
        render: (_, record) => <div>{record.customer.phone}</div>,
      },
      {
        title: "Số tiền",
        key: "amount_paid",
        width: 150,
        align: "right",
        render: (_, record) => {
          const amount_paid = Number(record.amount_paid || 0);
          return (
            <Text strong className="text-blue-600">
              {amount_paid.toLocaleString("vi-VN")} ₫
            </Text>
          );
        },
      },
      {
        title: "Trạng thái",
        key: "status",
        width: 130,
        align: "center",
        render: (_, record) => {
          const statusMap = {
            PAID: { color: "green", text: "Đã thanh toán" },
            UNPAID: { color: "orange", text: "Chờ thanh toán" },
            CANCELLED: { color: "red", text: "Đã hủy" },
          };
          const statusInfo = statusMap[record.status] || { color: "default", text: record.status };
          return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
        },
      },
      {
        title: "Thao tác",
        key: "actions",
        width: 140,
        fixed: "right",
        align: "center",
        render: (_, record) => (
          <Space size="small">
            <Button
              type="default"
              size="small"
              icon={<SendOutlined />}
              onClick={() => sendOne(record)}
              title="Gửi tin nhắc thanh toán"
              // loading={sendOneLoading === record.id || sendLoading === true}
              loading={sendOneLoading === record.id}
              disabled={["CANCELLED"].includes(record.status)}
            />
            <Button
              type="default"
              size="small"
              icon={<CreditCardOutlined />}
              onClick={() => pay(record)}
              title="Thanh toán"
              // disabled={["PAID", "CANCELLED"].includes(record.status)}
              disabled={["CANCELLED"].includes(record.status)}
            />
          </Space>
        ),
      },
    ];
  };

  const handleBack = () => {
    // navigate("/campains");
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/campains/${id}`);
  };

  const handleOrgInfo = (record) => {
    message.info(`Chi tiết thông tin đơn vị: ${record.organization?.name}`);
    navigate(`/organizations/information/${record.organization_id}`);
  };
  
  const handleCusInfo = (record) => {
    message.info(`Chi tiết thông tin khách hàng: ${record.customer?.name}`);
    navigate(`/customers/information/${record.customer_id}`);
  };

  const handleDelete = async () => {
    try {
      const confirmed = await confirmDelete({ entity: "đợt thu", name: campain.name });
      if (!confirmed) return;

      await campainAPIWithErrorHandling.deleteCampain(campain.id);
      message.success("Xóa đợt thu thành công");
      navigate("/campains");
    } catch (error) {
      message.error(error.message || "Không thể xóa đợt thu");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          height: "80vh",
          alignItems: "center",
        }}
      >
        <Spin size="large" tip="Đang tải dữ liệu chiến dịch..." />
      </div>
    );
  }

  if (!campain) {
    return (
      <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center">
        <Empty description="Không tìm thấy đợt thu" />
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const tableColumns = getTableColumns();

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      {/* <Card className="shadow-lg max-w-7xl mx-auto"> */}
      <Card className="shadow-lg mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            separator=">"
            className="mb-4"
            items={[
              {
                href: "/",
                title: (
                  <>
                    <HomeOutlined /> Trang chủ
                  </>
                ),
              },
              {
                href: "/campains",
                title: (
                  <>
                    <DollarOutlined /> Chiến dịch
                  </>
                ),
              },
              {
                title: (
                  <>
                    <InfoCircleOutlined /> Chi tiết
                  </>
                ),
              },
            ]}
          />

        </div>

        {/* Header Card */}
        <Card className="shadow-sm mb-6">
          <Row justify="space-between" align="middle">
            <Col>
              <Space direction="vertical" size={4}>
                <Title level={3} className="mb-0">
                  {campain.name}
                </Title>
                <Tag
                  color={statusInfo.color}
                  icon={statusInfo.icon}
                >
                  {statusInfo.text}
                </Tag>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                >
                  Quay lại
                </Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  disabled={campain.status !== "PLANNED" || campain.is_locked === true || campain.is_locked === 1}
                >
                  Chỉnh sửa
                </Button>
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete} disabled={campain.is_locked === true || campain.is_locked === 1}>
                  Xóa
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Statistics Cards */}
        <Spin spinning={statsLoading}>
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} sm={12} lg={8} xl={4.8}>
              <Card className="shadow-sm">
                <Statistic
                  title="Tổng khách hàng"
                  value={stats.totalCustomers || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: "#0070D7", fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={4.8}>
              <Card className="shadow-sm">
                <Statistic
                  title="Đã ủng hộ"
                  value={stats.totalPaid || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: "#128555", fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={4.8}>
              <Card className="shadow-sm">
                <Statistic
                  title="Chưa ủng hộ"
                  value={stats.totalPending || 0}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: "#A8681E", fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={12} xl={4.8}>
              <Card className="shadow-sm">
                <Statistic
                  title="Tỷ lệ ủng hộ"
                  value={stats.collectionRate || 0}
                  suffix="%"
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: "#656DC7", fontSize: 24 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={12} xl={4.8}>
              <Card className="shadow-sm">
                <Statistic
                  title="Số tiền thu được"
                  value={stats.totalAmountPaid || 0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  suffix="₫"
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: "#128555", fontSize: 24 }}
                />
              </Card>
            </Col>
          </Row>
        </Spin>

        {/* Two Column Layout for Info */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} lg={9}>
            <Card
              title={
                <>
                  <CalendarOutlined className="mr-2" />
                  Thông tin đợt thu
                </>
              }
              className="shadow-sm h-full"
            >
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Tên đợt thu">
                  <Text strong>{campain.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Ngày bắt đầu">
                  {dayjs(campain.start_date).format("DD/MM/YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày kết thúc">
                  {dayjs(campain.end_date).format("DD/MM/YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Thời gian diễn ra">
                  {dayjs(campain.end_date).diff(
                    dayjs(campain.start_date),
                    "day"
                  ) + 1}{" "}
                  ngày
                </Descriptions.Item>
                <Descriptions.Item label="T/K phát hành hóa đơn">
                  <Text strong>{campain?.invoice_cnf?.name}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <Card
              title={
                <>
                  <FileTextOutlined className="mr-2" />
                  Cấu hình gửi tin
                </>
              }
              className="shadow-sm h-full"
            >
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Thương hiệu">
                  {campain.zns_oa_name ? campain.zns_oa_name : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Template">
                  {campain.zns_template_id ? campain.zns_template_id : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Giờ gửi tin nhắc">
                  {campain.send_schedule_at
                    ? dayjs(campain.send_schedule_at, "HH:mm:ss").format(
                      "HH:mm"
                    )
                    : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Sau hạn">
                  {campain.after_date ?? "—"} ngày
                </Descriptions.Item>
                <Descriptions.Item label="Trước hạn">
                  {campain.before_date ?? "—"} ngày
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={6}>
            <Card
              title={
                <>
                  <ClockCircleOutlined className="mr-2" />
                  Lịch sử thay đổi
                </>
              }
              className="shadow-sm h-full"
            >
              <Timeline>
                <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                  <Text strong>Tạo đợt thu</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(campain.created_at).format("DD/MM/YYYY HH:mm")}
                  </Text>
                </Timeline.Item>

                {campain.updated_at !== campain.created_at && (
                  <Timeline.Item color="blue" dot={<EditOutlined />}>
                    <Text strong>Cập nhật lần cuối</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(campain.updated_at).format("DD/MM/YYYY HH:mm")}
                    </Text>
                  </Timeline.Item>
                )}

                <Timeline.Item color={statusInfo.color} dot={statusInfo.icon}>
                  <Text strong>Trạng thái hiện tại</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {statusInfo.text}
                  </Text>
                </Timeline.Item>
              </Timeline>
            </Card>
          </Col>

        </Row>

        {/* Content Card */}
        <Card
          title={
            <>
              <FileTextOutlined className="mr-2" />
              Nội dung tin nhắn
            </>
          }
          className="shadow-sm mb-6"
        >
          {campain.content ? (
            <div
              className="mb-0 p-3 bg-gray-50 rounded"
              dangerouslySetInnerHTML={{ __html: campain.content }}
              style={{
                lineHeight: 1.8,
              }}
            />
          ) : (
            <Text type="secondary">Chưa có nội dung</Text>
          )}
        </Card>

        <Card
          title={
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <span>
                <TeamOutlined className="mr-2" />
                Danh sách khách hàng
              </span>
              <Text type="secondary">
                Tổng: <strong>{campain.customer_fees?.length || 0}</strong>
              </Text>
            </Space>
          }
          className="shadow-sm"
        >
          {campain.customer_fees && campain.customer_fees.length > 0 ? (
            <Table
              dataSource={campain.customer_fees}
              columns={tableColumns}
              rowKey="customer_id"
              size="small"
              pagination={{
                // default*: ghim pageSize thì chọn số dòng/trang không có tác dụng
                defaultPageSize: 5,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} khách hàng`,
              }}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Empty
              description="Chưa có khách hàng nào trong đợt thu này"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      {/* </div> */}

      {/* Action Buttons */}
      <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-green-50">
        <Row justify="space-between" align="middle">
          <Col>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              size="default"
              className="shadow-sm"
            >
              Quay lại
            </Button>
          </Col>
          <Col>
            <Space>
              {/* <Button 
                  warning
                  loading={loadingTemplate}
                  onClick={() => {
                      handlePreviewTemplate();
                  }}
                  icon={<EyeOutlined />}
                  className="shadow-sm"
              >
                  Xem mẫu
              </Button> */}

              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEdit}
                size="default"
                className="shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                disabled={campain.status !== 'PLANNED' || campain.is_locked === true || campain.is_locked === 1}
              >
                Chỉnh sửa
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                className="shadow-sm"
                disabled={campain.is_locked === true || campain.is_locked === 1}
              >
                Xóa
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      
      </Card>

      {/* Payment Drawer */}
      <Drawer
        title={
          <Space>
            <CreditCardOutlined className="text-green-600" />
            <span>Thanh toán</span>
          </Space>
        }
        placement="right"
        width="min(640px, 100vw)"
        open={payModalVisible}
        onClose={() => {
          setPayModalVisible(false);
          payForm.resetFields();
          setSelectedCustomerFee(null);
        }}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          selectedCustomerFee ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
              <Button
                onClick={() => {
                  setPayModalVisible(false);
                  payForm.resetFields();
                }}
              >
                Hủy
              </Button>
              <Button
                type="primary"
                loading={payLoading}
                icon={<CreditCardOutlined />}
                onClick={() => payForm.submit()}
              >
                Xác nhận
              </Button>
            </div>
          ) : null
        }
      >
        {selectedCustomerFee && (
          <>
            <Descriptions column={1} size="small" className="mb-4" bordered>
              <Descriptions.Item label="Khách hàng">
                <Text strong>{selectedCustomerFee.customer?.name}</Text>
              </Descriptions.Item>
            </Descriptions>

            <Form form={payForm} layout="vertical" onFinish={handlePayment}>
              <Form.Item
                name="transaction_amount"
                label="Số tiền"
                rules={[
                  { required: true, message: "Vui lòng nhập số tiền" },
                  { type: "number", min: 0, message: "Số tiền phải lớn hơn 0" },
                ]}
              >
                <InputNumber
                  className="w-full"
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Nhập số tiền"
                  suffix="₫"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item name="note" label="Ghi chú">
                <TextArea
                  rows={3}
                  placeholder="Ghi chú về giao dịch (không bắt buộc)"
                  maxLength={255}
                  showCount
                />
              </Form.Item>

            </Form>
          </>
        )}
      </Drawer>

      {/* <Modal
        title={
          <Space>
            <CreditCardOutlined className="text-green-600" />
            <span>Chi tiết mẫu tin</span>
          </Space>
        }
        open={templatePreview}
        onCancel={() => {
          setTemplatePreview(null);
        }}
        footer={null}
        width="60%"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
            <div style={{
                fontSize: '12px',
                color: '#64748B',
                marginBottom: '8px'
            }}>
                Nội dung tin nhắn
            </div>
            <div style={{
                whiteSpace: 'pre-wrap',
                backgroundColor: 'white',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #CBD5E1',
                minHeight: '100px',
                fontSize: '14px',
                lineHeight: '1.6',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}>
                {templatePreview?.content}
            </div>
        </div>

        <div>
            <div style={{
                fontSize: '12px',
                color: '#64748B',
                marginBottom: '8px'
            }}>
                Tham số động ({templatePreview?.param.split(',').length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {templatePreview?.param.split(',').map((param, index) => (
                    <Tag
                        key={index}
                        color="blue"
                        style={{
                            margin: 0,
                            fontSize: '13px',
                            padding: '4px 12px'
                        }}
                    >
                        {param.trim()}
                    </Tag>
                ))}
            </div>
        </div>
    </Space>
      </Modal> */}
    </div>
  );
}